import { X } from 'lucide-react'
import type { NtArtifactOption } from '../../core/types'

/**
 * What students can make for the open house in a course. Every student makes
 * one artifact, in either their morning or their afternoon course, so every
 * course says what is on offer: a group poster of the week, a research
 * project, their own board game or app — group, solo, or either.
 */

export const MODE_LABEL: Record<NtArtifactOption['mode'], string> = { group: 'Group', solo: 'Solo', either: 'Group or solo' }

export function ArtifactList({ options, compact = false }: { options?: NtArtifactOption[]; compact?: boolean }) {
  if (!options?.length) return compact ? null : <p className="opt__hint">No artifact options listed yet.</p>
  return (
    <ul className="art__list">
      {options.map((o, i) => (
        <li key={i}><span className={`pill art__mode is-${o.mode}`}>{MODE_LABEL[o.mode]}</span> {o.description}</li>
      ))}
    </ul>
  )
}

export function ArtifactEditor({ options, readOnly, onChange }: {
  options: NtArtifactOption[]
  readOnly?: boolean
  onChange: (next: NtArtifactOption[]) => void
}) {
  const set = (i: number, o: Partial<NtArtifactOption>) => onChange(options.map((x, j) => (j === i ? { ...x, ...o } : x)))
  return (
    <section style={{ marginTop: 'var(--s-4)' }}>
      <h3 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-1)' }}>Artifact options <span className="req">*</span></h3>
      <p style={{ color: 'var(--ink-2)', fontSize: 'var(--t-sm)', margin: '0 0 var(--s-3)', lineHeight: 1.5 }}>
        Every student makes one artifact for the open house, in either their morning or their afternoon
        course. Say what students in yours can make, and whether it is a group or a solo piece: a poster
        board with photos from the week, a research project, their own board game or app.
      </p>
      {readOnly ? <ArtifactList options={options} /> : (
        <>
          <div style={{ display: 'grid', gap: 'var(--s-2)' }}>
            {options.map((o, i) => (
              <div key={i} className="art__row">
                <select className="opt__input" aria-label="Group or solo" value={o.mode}
                  onChange={(e) => set(i, { mode: e.target.value as NtArtifactOption['mode'] })}>
                  {(Object.keys(MODE_LABEL) as Array<NtArtifactOption['mode']>).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
                </select>
                <input className="opt__input" aria-label="What students make" value={o.description}
                  placeholder="e.g. A poster board with photos from each day"
                  onChange={(e) => set(i, { description: e.target.value })} />
                <button type="button" className="opt__icon opt__icon--danger" aria-label="Remove this option"
                  onClick={() => onChange(options.filter((_, j) => j !== i))}><X size={15} aria-hidden="true" /></button>
              </div>
            ))}
          </div>
          <button type="button" className="btn btn--ghost" style={{ marginTop: 'var(--s-2)', width: 'auto' }}
            onClick={() => onChange([...options, { mode: options.length ? 'solo' : 'group', description: '' }])}>
            + Add an artifact option
          </button>
        </>
      )}
    </section>
  )
}

/** Options with something written — what counts toward "has artifact options". */
export const realOptions = (o?: NtArtifactOption[]) => (o ?? []).filter((x) => x.description.trim())
